import * as React from 'react';
import { useState } from 'react';
import { signInWithEmailAndPassword, sendPasswordResetEmail } from 'firebase/auth';
import { auth } from '../../firebase';
import { motion } from 'motion/react';
import { Mail, Lock, AlertCircle } from 'lucide-react';
import { Logo } from '../Logo';

interface LoginPageProps {
  onSwitchToRegister: () => void;
}

export const LoginPage: React.FC<LoginPageProps> = ({ onSwitchToRegister }) => {
  const [view, setView] = useState<'login' | 'forgot'>('login');

  // Login state
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  // Forgot password state
  const [resetEmail, setResetEmail] = useState('');
  const [resetSent, setResetSent] = useState(false);
  const [resetError, setResetError] = useState<string | null>(null);
  const [resetLoading, setResetLoading] = useState(false);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      await signInWithEmailAndPassword(auth, email, password);
    } catch (err: any) {
      console.error("Login error:", err);
      setError(err.message || "Failed to login. Please check your credentials.");
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setResetLoading(true);
    setResetError(null);
    setResetSent(false);

    try {
      await sendPasswordResetEmail(auth, resetEmail);
      setResetSent(true);
    } catch (err: any) {
      console.error("Reset password error:", err);
      setResetError(err.message || "Failed to send reset link.");
    } finally {
      setResetLoading(false);
    }
  };

  const inputClass = "w-full pl-11 pr-4 py-3 bg-surface-2 border border-ink-200 rounded-md text-ink text-sm placeholder:text-ink-500 focus:border-oxblood focus:ring-2 focus:ring-oxblood/15 focus:bg-surface outline-none transition-all";
  const primaryBtn = "w-full py-3 bg-oxblood hover:bg-oxblood-800 text-paper text-sm font-medium tracking-wide rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed active:scale-[0.99]";

  if (view === 'forgot') {
    return (
      <div className="w-full max-w-md mx-auto p-4 flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="oi-overlay w-full p-8 md:p-10"
        >
          <div className="text-center mb-9">
            <div className="flex justify-center mb-6">
              <Logo variant="mark" size={54} />
            </div>
            <h1 className="text-2xl font-serif font-semibold text-ink">Reset Password</h1>
            <p className="text-xs font-medium uppercase tracking-[0.14em] text-ink-500 mt-3">Enter your email to receive a reset link</p>
          </div>

          {resetError && (
            <div className="mb-7 p-4 bg-[var(--color-danger-50)] border border-[var(--color-danger-100)] rounded-md flex items-start gap-3">
              <AlertCircle className="w-4 h-4 text-danger-600 shrink-0 mt-0.5" />
              <p className="text-xs text-danger-600 leading-relaxed">{resetError}</p>
            </div>
          )}

          {resetSent ? (
            <div className="mb-7 p-4 bg-[var(--color-success-50)] border border-[var(--color-success-100)] rounded-md">
              <p className="text-sm text-[var(--color-success-700)] leading-relaxed text-center">
                Check your email. A password reset link has been sent to {resetEmail}. Check your spam folder if you don't see it.
              </p>
            </div>
          ) : (
            <form onSubmit={handleResetPassword} className="space-y-5">
              <div>
                <label className="block text-xs font-medium text-ink-600 mb-2">Email Address</label>
                <div className="relative">
                  <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-500" />
                  <input
                    type="email"
                    required
                    value={resetEmail}
                    onChange={(e) => setResetEmail(e.target.value)}
                    className={inputClass}
                    placeholder="name@company.com"
                  />
                </div>
              </div>

              <button type="submit" disabled={resetLoading} className={primaryBtn}>
                {resetLoading ? "Sending..." : "Send Reset Link"}
              </button>
            </form>
          )}

          <div className="mt-8 text-center">
            <button
              type="button"
              onClick={() => setView('login')}
              className="text-xs text-ink-500 hover:text-oxblood transition-colors"
            >
              Back to Sign In
            </button>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-md mx-auto p-4 flex items-center justify-center">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="oi-overlay w-full p-8 md:p-10"
      >
        <div className="text-center mb-9">
          <div className="flex justify-center mb-6">
            <Logo variant="mark" size={54} />
          </div>
          <h1 className="text-2xl font-serif font-semibold text-ink">Welcome Back</h1>
          <p className="text-xs font-medium uppercase tracking-[0.14em] text-ink-500 mt-3">Sign in to access Oracle Insight</p>
        </div>

        {error && (
          <div className="mb-7 p-4 bg-[var(--color-danger-50)] border border-[var(--color-danger-100)] rounded-md flex items-start gap-3">
            <AlertCircle className="w-4 h-4 text-danger-600 shrink-0 mt-0.5" />
            <p className="text-xs text-danger-600 leading-relaxed">{error}</p>
          </div>
        )}

        <form onSubmit={handleLogin} className="space-y-5">
          <div>
            <label className="block text-xs font-medium text-ink-600 mb-2">Email Address</label>
            <div className="relative">
              <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-500" />
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={inputClass}
                placeholder="name@company.com"
              />
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-xs font-medium text-ink-600">Password</label>
              <button
                type="button"
                onClick={() => setView('forgot')}
                className="text-xs text-ink-500 hover:text-oxblood transition-colors"
              >
                Forgot password?
              </button>
            </div>
            <div className="relative">
              <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-500" />
              <input
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className={inputClass}
                placeholder="••••••••"
              />
            </div>
          </div>

          <button type="submit" disabled={loading} className={primaryBtn}>
            {loading ? "Authenticating..." : "Sign In"}
          </button>

          <div className="pt-2 text-center">
            <p className="text-xs text-ink-500">
              Don't have an account?{" "}
              <button
                type="button"
                onClick={onSwitchToRegister}
                className="text-oxblood font-medium hover:underline ml-1"
              >
                Request Access
              </button>
            </p>
          </div>
        </form>
      </motion.div>
    </div>
  );
};
